/**
 * Firebase Auth REST API wrapper for iOS workaround
 * 
 * On iOS, Firebase Auth SDK promises hang indefinitely.
 * This utility provides REST API alternatives for auth operations.
 */

const API_KEY = 'AIzaSyBq1eloUDVA2XfSlLuUCOdDVqdEoDeYfl4'; // From firebase config

interface SignUpResponse {
  idToken: string;
  email: string;
  refreshToken: string;
  expiresIn: string;
  localId: string; // This is the user ID
}

interface SignInResponse {
  idToken: string;
  email: string;
  refreshToken: string;
  expiresIn: string;
  localId: string;
  registered: boolean;
  displayName?: string;
}

/**
 * Sign up a new user using Firebase Auth REST API
 */
export async function signUpWithEmailAndPasswordRest(
  email: string,
  password: string
): Promise<SignUpResponse> {
  const url = `https://identitytoolkit.googleapis.com/v1/accounts:signUp?key=${API_KEY}`;
  
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), 10000); // 10 second timeout
  
  try {
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        email,
        password,
        returnSecureToken: true,
      }),
      signal: controller.signal,
    });
    
    clearTimeout(timeoutId);
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      console.error('Firebase Auth REST API signup error:', errorData);
      
      // Handle common error cases
      if (errorData.error?.message === 'EMAIL_EXISTS') {
        throw new Error('An account with this email already exists.');
      } else if (errorData.error?.message === 'WEAK_PASSWORD') {
        throw new Error('Password should be at least 6 characters.');
      } else if (errorData.error?.message === 'INVALID_EMAIL') {
        throw new Error('Invalid email address.');
      }
      
      throw new Error(errorData.error?.message || 'Failed to create account.');
    }
    
    const data = await response.json();
    console.log('[AuthRestAPI] User created successfully:', data.localId);
    return data;
  } catch (error: any) {
    clearTimeout(timeoutId);
    if (error.name === 'AbortError') {
      throw new Error('Request timed out. Please check your internet connection.');
    }
    throw error;
  }
}

/**
 * Sign in a user using Firebase Auth REST API
 */
export async function signInWithEmailAndPasswordRest(
  email: string,
  password: string
): Promise<SignInResponse> {
  const url = `https://identitytoolkit.googleapis.com/v1/accounts:signInWithPassword?key=${API_KEY}`;
  
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), 10000); // 10 second timeout
  
  try {
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        email,
        password,
        returnSecureToken: true,
      }),
      signal: controller.signal,
    });
    
    clearTimeout(timeoutId);
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      console.error('Firebase Auth REST API signin error:', errorData);
      
      // Handle common error cases
      if (errorData.error?.message === 'EMAIL_NOT_FOUND') {
        throw new Error('No account found with this email.');
      } else if (errorData.error?.message === 'INVALID_PASSWORD') {
        throw new Error('Incorrect password.');
      } else if (errorData.error?.message === 'USER_DISABLED') {
        throw new Error('This account has been disabled.');
      }
      
      throw new Error(errorData.error?.message || 'Failed to sign in.');
    }
    
    const data = await response.json();
    console.log('[AuthRestAPI] User signed in successfully:', data.localId);
    return data;
  } catch (error: any) {
    clearTimeout(timeoutId);
    if (error.name === 'AbortError') {
      throw new Error('Request timed out. Please check your internet connection.');
    }
    throw error;
  }
}

/**
 * Update user profile (display name) using Firebase Auth REST API
 */
export async function updateProfileRest(
  idToken: string,
  displayName: string
): Promise<void> {
  const url = `https://identitytoolkit.googleapis.com/v1/accounts:update?key=${API_KEY}`;
  
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), 10000);
  
  try {
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        idToken,
        displayName,
        returnSecureToken: false,
      }),
      signal: controller.signal,
    });
    
    clearTimeout(timeoutId);
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      console.error('Firebase Auth REST API update profile error:', errorData);
      throw new Error(errorData.error?.message || 'Failed to update profile.');
    }
    
    console.log('[AuthRestAPI] Profile updated successfully');
  } catch (error: any) {
    clearTimeout(timeoutId);
    if (error.name === 'AbortError') {
      throw new Error('Request timed out. Please check your internet connection.');
    }
    throw error;
  }
}

interface RefreshTokenResponse {
  id_token: string; // Note: Firebase uses snake_case in response
  refresh_token: string;
  expires_in: string;
  token_type: string;
  user_id: string;
  project_id: string;
}

/**
 * Refresh an expired idToken using a refresh token
 * Uses Firebase Secure Token API: https://securetoken.googleapis.com/v1/token
 */
export async function refreshIdToken(refreshToken: string): Promise<RefreshTokenResponse> {
  const url = `https://securetoken.googleapis.com/v1/token?key=${API_KEY}`;
  
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), 10000); // 10 second timeout
  
  try {
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body: `grant_type=refresh_token&refresh_token=${encodeURIComponent(refreshToken)}`,
      signal: controller.signal,
    });
    
    clearTimeout(timeoutId);
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      console.error('Firebase Auth REST API refresh token error:', errorData);
      
      // Handle common error cases
      if (errorData.error === 'invalid_grant' || errorData.error === 'TOKEN_EXPIRED') {
        throw new Error('REFRESH_TOKEN_EXPIRED');
      }
      
      throw new Error(errorData.error?.message || 'Failed to refresh token.');
    }
    
    const data = await response.json();
    console.log('[AuthRestAPI] Token refreshed successfully');
    return data;
  } catch (error: any) {
    clearTimeout(timeoutId);
    if (error.name === 'AbortError') {
      throw new Error('Request timed out. Please check your internet connection.');
    }
    // Re-throw REFRESH_TOKEN_EXPIRED error as-is
    if (error.message === 'REFRESH_TOKEN_EXPIRED') {
      throw error;
    }
    throw error;
  }
}

